import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { FaPhone, FaEnvelope, FaWhatsapp, FaMapMarkerAlt, FaFacebook, FaInstagram } from 'react-icons/fa';

const Contact = () => {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const message = `Name: ${formData.name}%0AEmail: ${formData.email}%0APhone: ${formData.phone}%0ASubject: ${formData.subject}%0A%0AMessage: ${formData.message}`;
    window.open(`https://wa.me/919420084600?text=${message}`, '_blank');
  };

  const contactMethods = [
    {
      icon: <FaPhone />,
      title: "Call Us",
      info: "+91 94200 84600",
      link: "tel:+919420084600",
      color: "from-blue-500 to-blue-600",
      description: "Available 24/7"
    },
    {
      icon: <FaWhatsapp />,
      title: "WhatsApp",
      info: "+91 94200 84600",
      link: "https://wa.me/919420084600",
      color: "from-green-500 to-green-600",
      description: "Quick Response"
    },
    {
      icon: <FaEnvelope />,
      title: "Email Us",
      info: "mohiteji@gmail.com",
      link: "mailto:mohiteji@gmail.com",
      color: "from-purple-500 to-purple-600",
      description: "Detailed Queries"
    },
    {
      icon: <FaMapMarkerAlt />,
      title: "Visit Us",
      info: "Laxmi Nagar, Nagpur",
      link: "#location",
      color: "from-red-500 to-red-600",
      description: "Get Directions"
    }
  ];

  return (
    <section id="contact" className="section-padding bg-white">
      <div className="max-w-7xl mx-auto">
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 50 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <motion.div
            animate={{ scale: [1, 1.2, 1] }}
            transition={{ duration: 2, repeat: Infinity }}
            className="inline-block mb-4"
          >
            <FaPhone className="text-primary-500 text-5xl mx-auto" />
          </motion.div>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Contact & Support
          </h2>
          <div className="w-24 h-1 bg-primary-500 mx-auto mb-6"></div>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Have questions or need assistance? We're here to help!
          </p>
        </motion.div>

        {/* Contact Methods Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
          {contactMethods.map((method, index) => (
            <motion.a
              key={index}
              href={method.link}
              target={method.link.startsWith('http') ? '_blank' : '_self'}
              rel="noopener noreferrer"
              initial={{ opacity: 0, y: 30 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: index * 0.1 }}
              whileHover={{ y: -10, scale: 1.03 }}
              className={`bg-gradient-to-br ${method.color} text-white rounded-2xl p-6 shadow-lg hover:shadow-2xl transition-all text-center group cursor-pointer`}
            >
              <motion.div
                className="text-5xl mb-4 flex justify-center"
                whileHover={{ scale: 1.2, rotate: 10 }}
              >
                {method.icon}
              </motion.div>
              <h3 className="text-xl font-bold mb-2">{method.title}</h3>
              <p className="text-sm mb-1 opacity-90">{method.info}</p>
              <p className="text-xs opacity-75">{method.description}</p>
            </motion.a>
          ))}
        </div>

        {/* Quick Enquiry Form */}
        <div className="grid lg:grid-cols-2 gap-12">
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ delay: 0.4 }}
          >
            <div className="bg-gradient-to-br from-primary-50 to-white rounded-2xl p-8 shadow-xl">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Quick Enquiry Form</h3>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-gray-700 font-medium mb-2">Your Name *</label>
                  <input
                    type="text"
                    name="name"
                    required
                    value={formData.name}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none transition"
                    placeholder="John Doe"
                  />
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-gray-700 font-medium mb-2">Email *</label>
                    <input
                      type="email"
                      name="email"
                      required
                      value={formData.email}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none transition"
                      placeholder="your@email.com"
                    />
                  </div>
                  <div>
                    <label className="block text-gray-700 font-medium mb-2">Phone *</label>
                    <input
                      type="tel"
                      name="phone"
                      required
                      value={formData.phone}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none transition"
                      placeholder="+91 98765 43210"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-gray-700 font-medium mb-2">Subject *</label>
                  <input
                    type="text"
                    name="subject"
                    required
                    value={formData.subject}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none transition"
                    placeholder="What can we help you with?"
                  />
                </div>

                <div>
                  <label className="block text-gray-700 font-medium mb-2">Message *</label>
                  <textarea
                    name="message"
                    required
                    value={formData.message}
                    onChange={handleChange}
                    rows="5"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none transition"
                    placeholder="Tell us more about your query..."
                  ></textarea>
                </div>

                <button type="submit" className="w-full btn-primary text-lg py-4">
                  Send Message
                </button>
              </form>
            </div>
          </motion.div>

          {/* Additional Info */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ delay: 0.4 }}
            className="space-y-6"
          >
            {/* Business Hours */}
            <div className="bg-white rounded-2xl p-8 shadow-lg border-2 border-primary-100">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Business Hours</h3>
              <div className="space-y-3 text-gray-700">
                <div className="flex justify-between">
                  <span className="font-medium">Monday - Friday:</span>
                  <span>9:00 AM - 8:00 PM</span>
                </div>
                <div className="flex justify-between">
                  <span className="font-medium">Saturday - Sunday:</span>
                  <span>9:00 AM - 6:00 PM</span>
                </div>
                <div className="pt-3 mt-3 border-t border-gray-200">
                  <p className="text-sm text-primary-600 font-semibold">
                    📞 Emergency Support: Available 24/7 for existing guests
                  </p>
                </div>
              </div>
            </div>

            {/* Response Time */}
            <div className="bg-gradient-to-br from-green-500 to-green-600 text-white rounded-2xl p-8 shadow-lg">
              <h3 className="text-2xl font-bold mb-4">Quick Response Guaranteed</h3>
              <div className="space-y-3">
                <div className="flex items-center space-x-3">
                  <div className="bg-white/20 p-2 rounded-lg">⚡</div>
                  <div>
                    <p className="font-semibold">WhatsApp</p>
                    <p className="text-sm opacity-90">Within 30 minutes</p>
                  </div>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="bg-white/20 p-2 rounded-lg">📧</div>
                  <div>
                    <p className="font-semibold">Email</p>
                    <p className="text-sm opacity-90">Within 2-4 hours</p>
                  </div>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="bg-white/20 p-2 rounded-lg">📞</div>
                  <div>
                    <p className="font-semibold">Phone</p>
                    <p className="text-sm opacity-90">Instant response</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Social Proof */}
            <div className="bg-gradient-to-br from-primary-500 to-primary-600 text-white rounded-2xl p-8 shadow-lg text-center">
              <p className="text-4xl font-bold mb-2">98%</p>
              <p className="text-lg">Response Rate</p>
              <p className="text-sm opacity-90 mt-2">We're known for our prompt and helpful responses</p>
            </div>
          </motion.div>
        </div>

        {/* Location Map & Info Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 0.5 }}
          className="mt-16"
        >
          <div className="text-center mb-12">
            <h3 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Our Location
            </h3>
            <div className="w-24 h-1 bg-primary-500 mx-auto mb-6"></div>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Centrally located in Laxmi Nagar, Nagpur for your convenience
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-8">
            {/* Address & Location Highlights */}
            <div className="space-y-6">
              {/* Address Card */}
              <div className="bg-white rounded-2xl p-8 shadow-lg">
                <div className="flex items-center space-x-3 mb-6">
                  <div className="bg-primary-500 text-white p-3 rounded-xl">
                    <FaMapMarkerAlt size={24} />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900">Our Address</h3>
                </div>
                <p className="text-gray-700 text-lg leading-relaxed mb-4">
                  Navyaa – The Homestay<br />
                  79, near NMC Zonal Office,<br />
                  opposite Vrundavan Apartments, Laxminagar,<br />
                  Nagpur, Maharashtra 440022<br />
                  India
                </p>
                <a
                  href="https://maps.app.goo.gl/yFjdfTFo665gjB788"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-primary inline-flex items-center space-x-2"
                >
                  <FaMapMarkerAlt />
                  <span>Get Directions</span>
                </a>
              </div>

              {/* Location Highlights */}
              <div className="bg-white rounded-2xl p-8 shadow-lg">
                <h3 className="text-xl font-bold text-gray-900 mb-4">Location Highlights</h3>
                <ul className="space-y-3 text-gray-700">
                  <li className="flex items-center space-x-3">
                    <span className="text-primary-500 text-xl">•</span>
                    <span>Close to Cafes</span>
                  </li>
                  <li className="flex items-center space-x-3">
                    <span className="text-primary-500 text-xl">•</span>
                    <span>Close to Restaurants & Hotels</span>
                  </li>
                  <li className="flex items-center space-x-3">
                    <span className="text-primary-500 text-xl">•</span>
                    <span>Close to Hospitals & Clinics</span>
                  </li>
                  <li className="flex items-center space-x-3">
                    <span className="text-primary-500 text-xl">•</span>
                    <span>Close to Banquets & Wedding Halls</span>
                  </li>
                  <li className="flex items-center space-x-3">
                    <span className="text-primary-500 text-xl">•</span>
                    <span>Close to Airport & Railway Station</span>
                  </li>
                </ul>
              </div>
            </div>

            {/* Map */}
            <div className="h-full min-h-[600px]">
              <div className="bg-white rounded-2xl shadow-lg overflow-hidden h-full">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3720.9584676923546!2d79.05923517527394!3d21.124020880523705!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3bd4c13e49cff67d%3A0xda8a1c9fe0ee0d00!2sNavyaa%20The%20Homestay!5e0!3m2!1sen!2sin!4v1737108000000!5m2!1sen!2sin"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Navyaa Location Map"
                  className="w-full h-full"
                ></iframe>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Social Media Links */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 0.6 }}
          className="mt-12 bg-gradient-to-r from-primary-500 to-nature-leaf text-white rounded-2xl p-8 text-center"
        >
          <h3 className="text-2xl font-bold mb-4">Connect With Us</h3>
          <p className="mb-6 text-lg">Follow us on social media for updates and special offers</p>
          <div className="flex justify-center space-x-6">
            <motion.a
              href="https://www.facebook.com/profile.php?id=61583258431411"
              target="_blank"
              rel="noopener noreferrer"
              whileHover={{ scale: 1.2, rotate: 5 }}
              className="bg-white/20 hover:bg-white/30 backdrop-blur-sm p-4 rounded-full transition-all"
            >
              <FaFacebook className="text-4xl" />
            </motion.a>
            <motion.a
              href="https://www.instagram.com/navya_thehomestay?igsh=MTkwNWg3ZjNyemFpdw=="
              target="_blank"
              rel="noopener noreferrer"
              whileHover={{ scale: 1.2, rotate: -5 }}
              className="bg-white/20 hover:bg-white/30 backdrop-blur-sm p-4 rounded-full transition-all"
            >
              <FaInstagram className="text-4xl" />
            </motion.a>
            <motion.a
              href="https://wa.me/919420084600"
              target="_blank"
              rel="noopener noreferrer"
              whileHover={{ scale: 1.2, rotate: 5 }}
              className="bg-white/20 hover:bg-white/30 backdrop-blur-sm p-4 rounded-full transition-all"
            >
              <FaWhatsapp className="text-4xl" />
            </motion.a>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Contact;
